import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Volume2, VolumeX, X, UserPlus, LogIn } from 'lucide-react';
import { readSession, writeSession } from '../lib/storage';
import { useEntryTimeline, T, easeOutCubic, easeInOutCubic } from './useEntryTimeline';

const STORAGE_KEY = 'fb_intro_played_v2';

// Re-export the constants so internal sub-components that reference them
// keep working without import churn.
export { easeOutCubic, easeInOutCubic };

// Silhouette SVG, scaled by phase
const Silhouette = ({ phase, growProgress }) => {
  // grow factors
  const bodyScaleX = 1 + growProgress * 1.4;   // 1 → 2.4
  const bodyScaleY = 1 + growProgress * 0.3;   // 1 → 1.3
  const headScale = 1 + growProgress * 0.25;   // 1 → 1.25
  const wobble = phase === 'preburst' ? 'fb-wobble' : '';

  return (
    <g className={wobble} style={{ transformOrigin: '150px 240px' }}>
      {/* Legs */}
      <rect x="132" y="280" width="14" height="80" fill="#0f172a" rx="3" />
      <rect x="154" y="280" width="14" height="80" fill="#0f172a" rx="3" />
      {/* Body */}
      <ellipse
        cx="150" cy="220"
        rx={48 * bodyScaleX} ry={70 * bodyScaleY}
        fill="#1e293b"
        style={{ transition: 'rx 80ms linear, ry 80ms linear' }}
      />
      {/* Arms (move outward as body grows) */}
      <ellipse
        cx={100 - growProgress * 28} cy={220} rx="14" ry="50" fill="#0f172a"
        style={{ transition: 'cx 80ms linear' }}
      />
      <ellipse
        cx={200 + growProgress * 28} cy={220} rx="14" ry="50" fill="#0f172a"
        style={{ transition: 'cx 80ms linear' }}
      />
      {/* Head */}
      <circle
        cx="150" cy={140 - growProgress * 6}
        r={28 * headScale}
        fill="#0f172a"
        style={{ transition: 'r 80ms linear, cy 80ms linear' }}
      />
    </g>
  );
};

// Burst particles
const Burst = ({ active }) => {
  const particles = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 22; i++) {
      const angle = (i / 22) * Math.PI * 2 + (Math.random() - 0.5) * 0.15;
      const distance = 180 + Math.random() * 180;
      const size = 8 + Math.random() * 18;
      arr.push({
        id: `p-${i}-${Math.random().toString(36).slice(2, 8)}`,
        dx: Math.cos(angle) * distance,
        dy: Math.sin(angle) * distance,
        size,
        delay: Math.random() * 80,
        hue: ['#ef4444', '#dc2626', '#b91c1c', '#f87171', '#991b1b'][i % 5],
      });
    }
    return arr;
  }, []);
  if (!active) return null;
  return (
    <>
      {particles.map((p) => (
        <span
          key={p.id}
          className="fb-particle"
          style={{
            '--dx': `${p.dx}px`,
            '--dy': `${p.dy}px`,
            '--sz': `${p.size}px`,
            background: p.hue,
            animationDelay: `${p.delay}ms`,
          }}
        />
      ))}
      <span className="fb-pop">POP!</span>
    </>
  );
};

// Auth choice modal that appears after burst
const AuthChoiceModal = ({ onClose }) => {
  const { customer } = useAuth();
  if (customer) return null;
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center px-4 fb-modal-in">
      <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-7 sm:p-9 text-center">
        <button onClick={onClose} aria-label="Close" className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-slate-100 text-slate-500">
          <X size={18} />
        </button>
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-50 text-red-700 text-[11px] font-bold tracking-[0.16em] uppercase">
          Welcome
        </div>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0a0a0a] mt-4 leading-tight">
          Time to bust some <span className="text-red-600">fat.</span>
        </h2>
        <p className="text-slate-500 mt-3 text-sm">Sign in or create your account to start shopping.</p>

        <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Link
            to="/account"
            onClick={onClose}
            className="inline-flex items-center justify-center gap-2 py-3.5 bg-red-500 hover:bg-red-400 text-white font-semibold rounded-lg transition-colors"
          >
            <UserPlus size={16} /> Create account
          </Link>
          <Link
            to="/account"
            onClick={onClose}
            className="inline-flex items-center justify-center gap-2 py-3.5 bg-white border border-slate-200 hover:border-red-300 text-[#0a0a0a] font-semibold rounded-lg transition-colors"
          >
            <LogIn size={16} /> Sign in
          </Link>
        </div>

        <p className="mt-5 text-[11px] text-slate-400 leading-relaxed">
          You must be 18+ and have an account to access this site.
        </p>
      </div>
    </div>
  );
};

const EntryAnimation = ({ onComplete }) => {
  const { customer } = useAuth();
  const [phase, setPhase] = useState('walking');
  const [translateX, setTranslateX] = useState(-220);
  const [growProgress, setGrowProgress] = useState(0);
  const [weight, setWeight] = useState(60);
  const [showModal, setShowModal] = useState(false);
  const startedAt = useRef(Date.now());

  // Latest customer/onComplete kept in refs so the rAF loop doesn't restart.
  const customerRef = useRef(customer);
  const onCompleteRef = useRef(onComplete);
  useEffect(() => { customerRef.current = customer; }, [customer]);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  const markPlayed = () => writeSession(STORAGE_KEY, '1');

  useEntryTimeline({
    startedAt, setPhase, setTranslateX, setGrowProgress, setWeight, setShowModal,
    customerRef, onCompleteRef, markPlayed,
  });

  const finish = () => { markPlayed(); onComplete(); };

  const skip = () => {
    setPhase('modal');
    setShowModal(true);
    setGrowProgress(0);
  };

  return (
    <div className="fixed inset-0 z-[80] bg-gradient-to-br from-slate-950 via-red-950 to-slate-950 overflow-hidden">
      {/* Glow / spotlight */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse at 50% 70%, rgba(251,146,60,0.18) 0%, transparent 60%)',
      }} />

      {/* Stage */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-red-300/80 text-[10px] tracking-[0.22em] uppercase mb-2 font-semibold animate-pulse">
          {phase === 'walking' && 'Approaching the scales...'}
          {phase === 'weighing' && 'Stepping on...'}
          {phase === 'growing' && 'Uh oh.'}
          {phase === 'preburst' && '...'}
          {(phase === 'bursting' || phase === 'modal') && 'Time for change.'}
        </div>

        {/* Weight readout */}
        <div className="mb-3 px-5 py-2 bg-black/40 border border-red-500/30 rounded-lg text-red-300 font-mono tabular-nums text-2xl sm:text-3xl tracking-wider shadow-lg shadow-red-500/10">
          {weight}<span className="text-base ml-1.5 text-red-300/60">kg</span>
        </div>

        {/* SVG stage */}
        <div className="relative" style={{ width: 'min(380px, 90vw)', aspectRatio: '380 / 440' }}>
          <svg viewBox="0 0 300 440" className="w-full h-full">
            {/* Floor */}
            <line x1="0" y1="395" x2="300" y2="395" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />

            {/* Scales platform */}
            <g>
              <rect x="90" y="370" width="120" height="20" rx="3" fill="#475569" stroke="#64748b" strokeWidth="1"/>
              <rect x="100" y="362" width="100" height="10" rx="2" fill="#cbd5e1"/>
              <rect x="110" y="356" width="80" height="8" rx="1.5" fill="#94a3b8"/>
              {/* Scales screen */}
              <rect x="125" y="338" width="50" height="16" rx="2" fill="#0f172a"/>
              <text x="150" y="350" textAnchor="middle" fontFamily="monospace" fontSize="9" fontWeight="700" fill="#ef4444">{weight}.0</text>
            </g>

            {/* Silhouette wrapper - translate for walk-in */}
            <g
              style={{
                transform: `translateX(${translateX}px)`,
                opacity: phase === 'bursting' || phase === 'modal' ? 0 : 1,
                transition: phase === 'bursting' ? 'opacity 200ms ease-out' : 'none',
              }}
            >
              <Silhouette phase={phase} growProgress={growProgress} />
            </g>
          </svg>

          {/* Burst particles overlay */}
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center" style={{ paddingBottom: '18%' }}>
            <div className="relative">
              <Burst active={phase === 'bursting' || phase === 'modal'} />
            </div>
          </div>
        </div>
      </div>

      {/* Skip button */}
      {!showModal && (
        <button
          onClick={skip}
          className="absolute top-5 right-5 z-10 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white/80 hover:text-white text-xs font-semibold tracking-wide rounded-lg backdrop-blur-sm border border-white/10"
        >
          Skip intro →
        </button>
      )}

      {/* Auth modal after burst */}
      {showModal && <AuthChoiceModal onClose={finish} />}
    </div>
  );
};

// Easings re-exported from useEntryTimeline above.

/** Wrapper that decides whether to play the animation. */
export const HomeIntroGate = () => {
  const { customer, loading } = useAuth();
  const [show, setShow] = useState(false);
  const [decided, setDecided] = useState(false);

  useEffect(() => {
    if (loading) return;
    // Play once per browser session, for every visitor (logged-in or not).
    // The wrapping <RequireCustomer> auth wall ensures only logged-in users
    // ever reach this point on production routes anyway.
    const played = readSession(STORAGE_KEY, null);
    if (!played) setShow(true);
    setDecided(true);
  }, [loading, customer]);

  if (!decided || !show) return null;

  return <EntryAnimation onComplete={() => setShow(false)} />;
};

export default EntryAnimation;
